import { relatedLinks, seoPageForPath, type SeoPage } from "../../shared/seo/pages";

/**
 * The prose under the board. It renders the same structure the build-time static
 * page emits, so the page a crawler renders with JavaScript says exactly what the
 * pre-rendered HTML said — and so the block is worth reading rather than being a
 * paragraph of keywords and a row of bare links.
 */
type Props =
  /** A fixed page, looked up by its route. */
  | { path: string; page?: undefined }
  /** A page built at runtime — an archive date, whose copy depends on its grid. */
  | { page: SeoPage; path?: undefined };

export function SeoContent({ path, page: provided }: Props) {
  const page = provided ?? (path === undefined ? undefined : seoPageForPath(path));
  if (!page) return null;
  const links = relatedLinks(page);

  return (
    <div className="page-notes">
      {page.sections.length > 0 && (
        <div className="seo-copy" aria-label="About this puzzle">
          {page.sections.map((section) => (
            <section key={section.heading}>
              <h2>{section.heading}</h2>
              {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            </section>
          ))}
        </div>
      )}

      {page.faqs && page.faqs.length > 0 && (
        <section className="page-faq">
          <h2>Common questions</h2>
          <dl>
            {page.faqs.map((faq) => (
              <div key={faq.question}>
                <dt>{faq.question}</dt>
                <dd>{faq.answer}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {links.length > 0 && (
        <nav className="related-links" aria-label="More nonograms">
          <h2>Keep playing</h2>
          <div>
            {links.map((link) => (
              <a href={link.href} key={link.href}>
                <span className="related-label">{link.label}</span>
                <span className="related-blurb">{link.blurb}</span>
              </a>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}
